import React, { useState, useEffect } from 'react';
import { 
  X, 
  Send, 
  Paperclip, 
  Database,
  ChevronDown,
  ChevronUp,
  AlertCircle
} from 'lucide-react';

interface Provider {
  id: string;
  name: string;
  specialty: string;
  avatarUrl?: string;
}

interface ComposeMessageProps {
  onCancel: () => void;
  onSend: (content: string, recipientId: string, subject: string) => void;
  onAttachData: () => void;
}

/**
 * ComposeMessage component for creating a new message to a healthcare provider
 */
const ComposeMessage: React.FC<ComposeMessageProps> = ({
  onCancel,
  onSend,
  onAttachData
}) => {
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [selectedProviderId, setSelectedProviderId] = useState('');
  const [isUrgent, setIsUrgent] = useState(false);
  const [providers, setProviders] = useState<Provider[]>([]);
  const [isProvidersDropdownOpen, setIsProvidersDropdownOpen] = useState(false);
  const [formErrors, setFormErrors] = useState({
    provider: false,
    subject: false,
    message: false
  });
  
  // Mock providers data - in a real app, this would come from an API
  useEffect(() => {
    const mockProviders: Provider[] = [
      {
        id: 'provider-001',
        name: 'Dr. Sarah Johnson',
        specialty: 'Primary Care',
        avatarUrl: 'https://randomuser.me/api/portraits/women/65.jpg'
      },
      {
        id: 'provider-002',
        name: 'Dr. Michael Chen',
        specialty: 'Cardiology',
        avatarUrl: 'https://randomuser.me/api/portraits/men/45.jpg'
      },
      {
        id: 'provider-003',
        name: 'Dr. Emily Rodriguez',
        specialty: 'Dermatology',
        avatarUrl: 'https://randomuser.me/api/portraits/women/28.jpg'
      },
      {
        id: 'provider-004',
        name: 'Dr. Robert Williams',
        specialty: 'Neurology',
        avatarUrl: 'https://randomuser.me/api/portraits/men/67.jpg'
      }
    ];
    
    setProviders(mockProviders);
  }, []);
  
  const handleSendMessage = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate form
    const errors = {
      provider: !selectedProviderId,
      subject: !subject.trim(),
      message: !message.trim()
    };
    
    setFormErrors(errors);
    
    if (!errors.provider && !errors.subject && !errors.message) {
      onSend(message, selectedProviderId, subject);
    }
  };
  
  const selectedProvider = providers.find(provider => provider.id === selectedProviderId);
  
  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-[var(--border-subtle)]">
        <h2 className="font-medium text-lg text-[var(--text-primary)]">New Message</h2>
        <button 
          onClick={onCancel}
          className="text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
          aria-label="Cancel"
        >
          <X size={20} />
        </button>
      </div>
      
      {/* Compose form */}
      <form onSubmit={handleSendMessage} className="flex-1 flex flex-col p-4 overflow-y-auto">
        {/* Provider selection */}
        <div className="mb-4">
          <label htmlFor="provider" className="block text-sm font-medium text-[var(--text-secondary)] mb-1">
            To:
          </label>
          <div className="relative">
            <button
              type="button"
              onClick={() => setIsProvidersDropdownOpen(!isProvidersDropdownOpen)}
              className={`
                w-full p-3 border rounded-lg flex items-center justify-between
                ${formErrors.provider 
                  ? 'border-[var(--status-error)] bg-[var(--status-error-light)]' 
                  : 'border-[var(--border-subtle)] bg-[var(--background-surface)]'
                }
                text-[var(--text-primary)]
              `}
            >
              {selectedProvider ? (
                <div className="flex items-center">
                  {selectedProvider.avatarUrl ? (
                    <img 
                      src={selectedProvider.avatarUrl} 
                      alt={selectedProvider.name}
                      className="w-6 h-6 rounded-full mr-2 object-cover"
                    />
                  ) : (
                    <div className="w-6 h-6 rounded-full bg-[var(--background-subtle)] flex items-center justify-center mr-2">
                      <span className="text-xs text-[var(--text-secondary)] font-semibold">
                        {selectedProvider.name.charAt(0)}
                      </span>
                    </div>
                  )}
                  <span>{selectedProvider.name}</span>
                  <span className="ml-2 text-xs text-[var(--text-secondary)]">
                    ({selectedProvider.specialty})
                  </span>
                </div>
              ) : (
                <span className="text-[var(--text-secondary)]">Select a provider</span>
              )}
              
              {isProvidersDropdownOpen ? (
                <ChevronUp size={18} className="text-[var(--text-secondary)]" />
              ) : (
                <ChevronDown size={18} className="text-[var(--text-secondary)]" />
              )}
            </button>
            
            {formErrors.provider && (
              <p className="mt-1 text-xs text-[var(--status-error)]">
                Please select a provider
              </p>
            )}
            
            {isProvidersDropdownOpen && (
              <div className="absolute z-10 mt-1 w-full bg-[var(--background-surface)] border border-[var(--border-subtle)] rounded-lg shadow-lg max-h-60 overflow-y-auto">
                {providers.map((provider) => (
                  <button
                    key={provider.id}
                    type="button"
                    onClick={() => {
                      setSelectedProviderId(provider.id);
                      setIsProvidersDropdownOpen(false);
                      setFormErrors({ ...formErrors, provider: false });
                    }}
                    className={`
                      w-full p-3 flex items-center hover:bg-[var(--background-surface-hover)] text-left
                      ${provider.id === selectedProviderId ? 'bg-[var(--brand-primary-subtle)]' : ''}
                    `}
                  >
                    {provider.avatarUrl ? (
                      <img 
                        src={provider.avatarUrl} 
                        alt={provider.name}
                        className="w-8 h-8 rounded-full mr-3 object-cover"
                      />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-[var(--background-subtle)] flex items-center justify-center mr-3">
                        <span className="text-sm text-[var(--text-secondary)] font-semibold">
                          {provider.name.charAt(0)}
                        </span>
                      </div>
                    )}
                    <div>
                      <div className="font-medium text-[var(--text-primary)]">
                        {provider.name}
                      </div>
                      <div className="text-xs text-[var(--text-secondary)]">
                        {provider.specialty}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
        
        {/* Subject line */}
        <div className="mb-4">
          <label htmlFor="subject" className="block text-sm font-medium text-[var(--text-secondary)] mb-1">
            Subject:
          </label>
          <input
            type="text"
            id="subject"
            value={subject}
            onChange={(e) => {
              setSubject(e.target.value);
              if (e.target.value.trim()) {
                setFormErrors({ ...formErrors, subject: false });
              }
            }}
            className={`
              w-full p-3 border rounded-lg
              ${formErrors.subject 
                ? 'border-[var(--status-error)] bg-[var(--status-error-light)]' 
                : 'border-[var(--border-subtle)] bg-[var(--background-surface)]'
              }
              text-[var(--text-primary)]
            `}
            placeholder="Enter a subject"
          />
          {formErrors.subject && (
            <p className="mt-1 text-xs text-[var(--status-error)]">
              Please enter a subject
            </p>
          )}
        </div>
        
        {/* Urgent checkbox */}
        <div className="mb-4">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={isUrgent}
              onChange={(e) => setIsUrgent(e.target.checked)}
              className="mr-2"
            />
            <span className="text-sm flex items-center">
              <AlertCircle size={14} className="mr-1 text-[var(--status-error)]" />
              Mark as urgent (only for time-sensitive medical concerns)
            </span>
          </label>
        </div>
        
        {/* Message text */}
        <div className="mb-4 flex-1">
          <label htmlFor="message" className="block text-sm font-medium text-[var(--text-secondary)] mb-1">
            Message:
          </label>
          <textarea
            id="message"
            value={message}
            onChange={(e) => {
              setMessage(e.target.value);
              if (e.target.value.trim()) {
                setFormErrors({ ...formErrors, message: false });
              }
            }}
            className={`
              w-full p-3 border rounded-lg h-full min-h-[200px]
              ${formErrors.message 
                ? 'border-[var(--status-error)] bg-[var(--status-error-light)]' 
                : 'border-[var(--border-subtle)] bg-[var(--background-surface)]'
              }
              text-[var(--text-primary)] resize-none
            `}
            placeholder="Type your message here..."
          />
          {formErrors.message && (
            <p className="mt-1 text-xs text-[var(--status-error)]">
              Please enter a message
            </p>
          )}
        </div>
        
        <div className="flex items-center mt-auto">
          <div className="flex-1">
            <button
              type="button"
              onClick={onAttachData}
              className="inline-flex items-center text-sm text-[var(--brand-primary)] hover:text-[var(--brand-primary-hover)] mr-4"
            >
              <Database size={16} className="mr-1" />
              Attach health data
            </button>
            
            <button
              type="button"
              className="inline-flex items-center text-sm text-[var(--brand-primary)] hover:text-[var(--brand-primary-hover)]"
            >
              <Paperclip size={16} className="mr-1" />
              Attach files
            </button>
          </div>
          
          <div className="flex space-x-3">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 border border-[var(--border-subtle)] rounded-lg text-[var(--text-primary)] hover:bg-[var(--background-surface-hover)]"
            >
              Cancel
            </button>
            
            <button
              type="submit"
              className="px-4 py-2 bg-[var(--brand-primary)] text-white rounded-lg hover:bg-[var(--brand-primary-hover)] flex items-center"
            >
              <Send size={16} className="mr-2" />
              Send Message
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default ComposeMessage; 